import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { supabase } from '../config/supabaseConfig.ts'

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

const UNAUTHORIZED_MSG = 'Acceso no autorizado';

// Mapea el usuario de Supabase a un objeto compatible con los componentes existentes.
// El rol 'admin' SOLO se asigna si la BD lo confirma (platform_admins vía RPC);
// user_metadata.role es editable por el usuario y solo se conserva como dato de despliegue.
const mapUser = (user, isAdmin) => {
  return {
    ...user,
    full_name: user.user_metadata?.full_name || user.email,
    metadata_role: user.user_metadata?.role || null,
    role: isAdmin ? 'admin' : null,
  };
};

// Consulta a la BD si el usuario del JWT actual es admin de plataforma
const checkIsPlatformAdmin = async () => {
  const { data, error } = await supabase.rpc('is_platform_admin');
  if (error) {
    console.error('[AuthContext] Error al verificar admin:', error);
    return false;
  }
  return data === true;
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Contador para descartar verificaciones obsoletas (eventos de auth encadenados)
  const checkSeq = useRef(0);
  // auth_user_id ya verificado como admin (evita re-consultar en cada refresh de token)
  const verifiedAdminId = useRef(null);

  // Login solo con Supabase Auth. El fallback a login-proxy (migración de hashes
  // bcrypt legacy) se retiró: permitía revivir contraseñas viejas y devolvía tokens.
  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      const isInvalidCredentials =
        error.message?.toLowerCase().includes('invalid login credentials') ||
        error.code === 'invalid_credentials' ||
        error.status === 400;
      throw isInvalidCredentials ? new Error('Credenciales inválidas') : error;
    }

    return ensureAdmin(data.user);
  };

  // Verifica en BD que el usuario autenticado sea admin; si no, cierra sesión
  const ensureAdmin = async (user) => {
    const isAdmin = await checkIsPlatformAdmin();
    if (!isAdmin) {
      await supabase.auth.signOut();
      throw new Error(UNAUTHORIZED_MSG);
    }
    return user;
  };

  // Logout con Supabase Auth
  const logout = async () => {
    await supabase.auth.signOut();
  };

  // Resuelve la sesión: solo expone currentUser si la BD confirma que es admin
  const resolveSession = async (session) => {
    const seq = ++checkSeq.current;
    if (!session) {
      verifiedAdminId.current = null;
      setCurrentUser(null);
      setLoading(false);
      return;
    }
    if (verifiedAdminId.current === session.user.id) {
      setCurrentUser(mapUser(session.user, true));
      setLoading(false);
      return;
    }
    const isAdmin = await checkIsPlatformAdmin();
    if (seq !== checkSeq.current) return; // llegó un evento más reciente
    if (!isAdmin) {
      verifiedAdminId.current = null;
      setCurrentUser(null);
      setLoading(false);
      await supabase.auth.signOut();
      return;
    }
    verifiedAdminId.current = session.user.id;
    setCurrentUser(mapUser(session.user, true));
    setLoading(false);
  };

  // Inicializar sesión y suscribirse a cambios de auth
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      resolveSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // Diferido: no llamar a supabase (rpc) dentro del callback para evitar bloqueos de supabase-js
      setTimeout(() => resolveSession(session), 0);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Context value
  const value = {
    currentUser,
    login,
    logout,
    isAuthenticated: !!currentUser,
    role: currentUser?.role || null,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
